/**
 * Sıkıştırmasız (stored) ZIP yazıcı — arşiv dışa aktarımı için. Dosyalar
 * zaten sıkıştırılmış (mp3) ya da küçük metinler; sıkıştırma kazandırmıyor.
 * İçerik Blob parçası olarak tutuluyor: büyük ses dosyaları JS belleğine
 * kopyalanmıyor, tarayıcı gerekirse diske alıyor. Toplam 4 GB'ı aşmamalı
 * (ZIP64 yok).
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

async function crc32(blob: Blob): Promise<number> {
  let crc = 0xffffffff;
  const reader = blob.stream().getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    for (let i = 0; i < value.length; i++) crc = CRC_TABLE[(crc ^ value[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function dosDateTime(date: Date): { time: number; date: number } {
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2),
    date: ((Math.max(1980, date.getFullYear()) - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

interface Entry {
  name: Uint8Array<ArrayBuffer>;
  crc: number;
  size: number;
  offset: number;
  time: number;
  date: number;
}

export class ZipBuilder {
  private parts: BlobPart[] = [];
  private entries: Entry[] = [];
  private offset = 0;
  private readonly encoder = new TextEncoder();

  async add(path: string, content: Blob | string): Promise<void> {
    const blob = typeof content === 'string' ? new Blob([content], { type: 'text/plain' }) : content;
    const name = this.encoder.encode(path);
    const crc = await crc32(blob);
    const { time, date } = dosDateTime(new Date());

    const header = new DataView(new ArrayBuffer(30));
    header.setUint32(0, 0x04034b50, true);
    header.setUint16(4, 20, true);
    header.setUint16(6, 0x0800, true); // UTF-8 adlar
    header.setUint16(8, 0, true); // stored
    header.setUint16(10, time, true);
    header.setUint16(12, date, true);
    header.setUint32(14, crc, true);
    header.setUint32(18, blob.size, true);
    header.setUint32(22, blob.size, true);
    header.setUint16(26, name.length, true);
    header.setUint16(28, 0, true);

    this.entries.push({ name, crc, size: blob.size, offset: this.offset, time, date });
    this.parts.push(header.buffer, name, blob);
    this.offset += 30 + name.length + blob.size;
  }

  toBlob(): Blob {
    const central: BlobPart[] = [];
    let centralSize = 0;
    for (const entry of this.entries) {
      const record = new DataView(new ArrayBuffer(46));
      record.setUint32(0, 0x02014b50, true);
      record.setUint16(4, 20, true);
      record.setUint16(6, 20, true);
      record.setUint16(8, 0x0800, true);
      record.setUint16(10, 0, true);
      record.setUint16(12, entry.time, true);
      record.setUint16(14, entry.date, true);
      record.setUint32(16, entry.crc, true);
      record.setUint32(20, entry.size, true);
      record.setUint32(24, entry.size, true);
      record.setUint16(28, entry.name.length, true);
      record.setUint32(42, entry.offset, true);
      central.push(record.buffer, entry.name);
      centralSize += 46 + entry.name.length;
    }
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true);
    end.setUint16(8, this.entries.length, true);
    end.setUint16(10, this.entries.length, true);
    end.setUint32(12, centralSize, true);
    end.setUint32(16, this.offset, true);
    return new Blob([...this.parts, ...central, end.buffer], { type: 'application/zip' });
  }
}

/** Blob'u dosya olarak indirir. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
}
